import { jsx, jsxs } from "react/jsx-runtime";
import MListItem from "@mui/material/ListItem";
import MListItemButton from "@mui/material/ListItemButton";
import MListItemIcon from "@mui/material/ListItemIcon";
import MListItemText from "@mui/material/ListItemText";
import styled from "@mui/material/styles/styled";
import { EAvatarSize } from "../AvatarSimple";
import isFunction from "lodash/isFunction";
import toNumber from "lodash/toNumber";
import React from "react";
import { useParentBg } from "../../shared/hooks/current-color";
import Box from "../Box";
import Checkbox, { ECheckboxSize } from "../Checkbox";
import { Icon } from "../Icon";
import IconButton from "../IconButton";
import Radio, { ERadioSize } from "../Radio";
import Stack from "../Stack";
var EListItemSize = /* @__PURE__ */ ((EListItemSize2) => {
  EListItemSize2["M"] = "M";
  EListItemSize2["L"] = "L";
  return EListItemSize2;
})(EListItemSize || {});
const AVAILABLE_SIZE = Object.values(EListItemSize);
var EListItemControl = /* @__PURE__ */ ((EListItemControl2) => {
  EListItemControl2["RADIO"] = "radio";
  EListItemControl2["CHECKBOX"] = "checkbox";
  return EListItemControl2;
})(EListItemControl || {});
var EListItemIconPosition = /* @__PURE__ */ ((EListItemIconPosition2) => {
  EListItemIconPosition2["MARGIN_RIGHT"] = "marginRight";
  EListItemIconPosition2["MARGIN_LEFT"] = "marginLeft";
  return EListItemIconPosition2;
})(EListItemIconPosition || {});
const MOCK_TOKENS = {
  size: {
    default: {
      M: "40",
      L: "48"
    },
    extra: {
      M: "48",
      L: "56"
    }
  },
  typography: {
    title: {
      M: "body2",
      L: "body1"
    },
    subTitle: {
      M: "caption1",
      L: "caption1"
    }
  },
  padding: {
    top: {
      M: "6",
      L: "8"
    },
    bottom: {
      M: "6",
      L: "8"
    },
    left: {
      M: "12",
      L: "12"
    },
    right: {
      M: "12",
      L: "12"
    }
  },
  icon: {
    margin: {
      M: "8",
      L: "12"
    },
    size: {
      M: "16",
      L: "24"
    }
  }
};
const END_ICON_WRAPPER_SX = {
  display: "flex",
  alignItems: "center",
  alignSelf: "center"
};
const CustomListItem = styled(MListItem)((props) => {
  const {
    theme,
    size,
    disabled,
    subTitle,
    active,
    hMargin,
    isHeader,
    color,
    bg: bgProps
  } = props;
  const parentBg = useParentBg();
  const bg = bgProps ?? parentBg ?? "elevation2";
  const palette = theme.palette;
  const bgColor = palette[bg].main;
  const hoverBgColor = palette[bg].hover;
  const defaultColor = palette[bg][color] ?? palette[bg].contrast;
  const secondaryDefaultColor = palette[bg].lowContrast;
  const disableColor = palette[bg].disableText;
  const listItemHeight = subTitle ? `${MOCK_TOKENS.size.extra[size]}px` : `${MOCK_TOKENS.size.default[size]}px`;
  const paddingTop = `${MOCK_TOKENS.padding.top[size]}px`;
  const paddingBottom = `${MOCK_TOKENS.padding.bottom[size]}px`;
  const paddingLeft = `${hMargin || MOCK_TOKENS.padding.left[size]}px`;
  const paddingRight = `${hMargin || MOCK_TOKENS.padding.right[size]}px`;
  return {
    color: defaultColor,
    // backgroundColor: bgColor,
    minHeight: listItemHeight,
    maxHeight: listItemHeight,
    overflow: "hidden",
    alignItems: subTitle ? "flex-start" : "",
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    ...!subTitle && {
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    ...active && !disabled && {
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: hoverBgColor
      }
    },
    ...!isHeader && !disabled && !active && {
      "&:hover::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: hoverBgColor
      }
    },
    ...disabled && {
      pointerEvents: "none",
      color: disableColor
    },
    ...isHeader ? {
      color: palette.elevation2.lowContrast,
      cursor: "default"
    } : {
      cursor: "pointer"
    },
    "& .MuiListItemText-secondary": {
      color: disabled ? disableColor : secondaryDefaultColor
    },
    "&.Mui-disabled": {
      opacity: 1
    }
  };
});
const CustomItemIcon = styled(MListItemIcon)(({ size, position }) => {
  const itemIconSize = MOCK_TOKENS.icon.size[size];
  const margin = MOCK_TOKENS.icon.margin[size];
  return {
    "&.MuiListItemIcon-root": {
      minHeight: `${itemIconSize}px`,
      maxHeight: `${itemIconSize}px`,
      minWidth: `${itemIconSize}px`,
      maxWidth: `${itemIconSize}px`,
      [position]: `${margin}px`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      alignSelf: "center"
    }
  };
});
const CustomListItemText = styled(MListItemText)(({ theme, size, subTitle, isHeader }) => {
  const primaryTypography = theme.typography[MOCK_TOKENS.typography.title[size]];
  const secondaryTypography = theme.typography[MOCK_TOKENS.typography.subTitle[size]];
  const extraTopMargin = `${toNumber(MOCK_TOKENS.padding.top[size]) / 2}px`;
  return {
    "&.MuiListItemText-root": {
      margin: "0px",
      fontSize: "inherit",
      marginTop: subTitle && size === "M" /* M */ ? extraTopMargin : ""
    },
    "& > .MuiListItemText-primary": {
      ...primaryTypography,
      ...isHeader && {
        textTransform: "uppercase"
      },
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    },
    "& > .MuiListItemText-secondary": {
      ...secondaryTypography,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    }
  };
});
const CustomListItemButton = styled(MListItemButton)(({ isHeader }) => {
  return {
    "&.MuiListItemButton-root": {
      overflow: "hidden",
      alignSelf: "center",
      padding: "0",
      "&:hover": {
        background: "none"
      },
      ...isHeader && {
        cursor: "default"
      }
    }
  };
});
const AVATAR_SX = {
  boxSizing: "border-box",
  alignSelf: "center"
};
const AvatarContainer = ({ avatar, size }) => {
  const isMSize = size === "M" /* M */;
  return /* @__PURE__ */ jsx(Box, { sx: AVATAR_SX, className: "avatar-container", marginRight: isMSize ? "6px" : "8px", children: React.cloneElement(avatar, { size: isMSize ? EAvatarSize.XS : EAvatarSize.S }) });
};
const ListItem = (props) => {
  const {
    size: sizeRaw = "M" /* M */,
    disabled = false,
    title,
    subTitle,
    startIcon,
    avatar,
    endIcon,
    control,
    checked,
    active,
    extraChild,
    onClick,
    onEndIconClick,
    hMargin,
    isHeader,
    color: propsColor,
    className
  } = props;
  const size = AVAILABLE_SIZE.includes(sizeRaw) ? sizeRaw : "M" /* M */;
  const iconSize = size === "L" /* L */ ? "l" : "s";
  const showControl = control && !startIcon;
  const showCheckbox = control === "checkbox" /* CHECKBOX */;
  const color = propsColor ?? "contrast";
  const handleClickIcon = React.useCallback((e) => {
    e.stopPropagation();
    if (isFunction(onEndIconClick))
      onEndIconClick();
  }, [onEndIconClick]);
  const handleClick = React.useCallback(() => {
    if (isFunction(onClick))
      onClick();
  }, [onClick]);
  return /* @__PURE__ */ jsxs(
    CustomListItem,
    {
      disablePadding: true,
      size,
      disabled,
      subTitle: isHeader ? void 0 : subTitle,
      active,
      onClick: handleClick,
      hMargin,
      isHeader,
      color,
      className,
      children: [
        startIcon && !isHeader && /* @__PURE__ */ jsx(CustomItemIcon, { size, position: "marginRight" /* MARGIN_RIGHT */, children: /* @__PURE__ */ jsx(Icon, { icon: startIcon, size: iconSize, color }) }),
        showControl && !isHeader && /* @__PURE__ */ jsx(CustomItemIcon, { size, position: "marginRight" /* MARGIN_RIGHT */, children: showCheckbox ? /* @__PURE__ */ jsx(Checkbox, { size: ECheckboxSize.S, checked, disabled }) : /* @__PURE__ */ jsx(Radio, { size: ERadioSize.S, checked, disabled }) }),
        avatar && !isHeader ? /* @__PURE__ */ jsx(AvatarContainer, { avatar, size }) : null,
        /* @__PURE__ */ jsx(CustomListItemButton, { size, isHeader, children: /* @__PURE__ */ jsx(CustomListItemText, { size, primary: title, secondary: isHeader ? null : subTitle, isHeader }) }),
        (endIcon || extraChild) && !isHeader && /* @__PURE__ */ jsxs(Stack, { direction: "row", pl: "12px", spacing: "12px", sx: END_ICON_WRAPPER_SX, children: [
          extraChild,
          endIcon && /* @__PURE__ */ jsx(IconButton, { onClick: handleClickIcon, children: /* @__PURE__ */ jsx(CustomItemIcon, { size, children: /* @__PURE__ */ jsx(Icon, { icon: endIcon, size: iconSize, color }) }) })
        ] })
      ]
    }
  );
};
var ListItem_default = ListItem;
export {
  EListItemControl,
  EListItemSize,
  ListItem_default as default
};
